/**
 * colorScales.ts
 * مقاييس ألوان الطقس — مُعايَرة لتطابق Zoom Earth تماماً
 * المصدر: صور التدرّج PNG (512px) المضمّنة في CSS شريط مفتاح ألوان zoom.earth نفسه،
 * معايَرةً بمواضع أرقام الشريط (استخراج آلي 2026-07-07) — لا تقدير بصري.
 * الاستثناءان: pressure و clouds (لا مرجع لوني لهما لدى Zoom Earth — الضغط لديهم isolines فقط).
 *
 * القيم أدناه هي *الافتراضيات*؛ وقت التشغيل تُقرأ الألوان من سجلّ قابل للتعديل
 * (STOPS_REGISTRY) يُرطَّب من إعدادات الأدمن عبر /api/v1/config قبل تركيب الخريطة.
 */

import type { ForecastGridType } from '../config/weatherLayers';
// مدى ترميز كل طبقة — يُستعمل لحراسة التدرّجات القادمة من لوحة الأدمن (راجع setColorStops).
import { VALUE_RANGES } from '../components/WeatherMap/webgl/weatherTextures';
import radarPalette from '../config/radarPalette.json';

export type RgbaTuple = [number, number, number, number];
export type ColorStop = [number, RgbaTuple];

function clamp01(value: number): number {
    return Math.max(0, Math.min(1, value));
}

function lerpColor(a: RgbaTuple, b: RgbaTuple, t: number): RgbaTuple {
    const x = clamp01(t);
    return [
        Math.round(a[0] + (b[0] - a[0]) * x),
        Math.round(a[1] + (b[1] - a[1]) * x),
        Math.round(a[2] + (b[2] - a[2]) * x),
        a[3] + (b[3] - a[3]) * x,
    ];
}

function interpolateStops(value: number, stops: ColorStop[]): RgbaTuple {
    if (stops.length === 0) return [255, 255, 255, 0];
    if (value <= stops[0][0]) return stops[0][1];

    for (let index = 0; index < stops.length - 1; index += 1) {
        const current = stops[index];
        const next = stops[index + 1];
        if (value <= next[0]) {
            const range = next[0] - current[0] || 1;
            return lerpColor(current[1], next[1], (value - current[0]) / range);
        }
    }

    return stops[stops.length - 1][1];
}

function toRgba(color: RgbaTuple): string {
    return `rgba(${color[0]},${color[1]},${color[2]},${color[3].toFixed(3)})`;
}

// ─────────────────────────────────────────────────────────────────────────────
// WIND SPEED (سرعة الرياح)
//
// **لوحة الألوان الخام** — تسلسل الألوان كما هو، بلا أي تغيير. لا تُقرأ مباشرةً:
// WIND_PALETTE أدناه يُعيد توزيعها على محور Windy. مؤشّر هذه القائمة (0..100)
// صار **موضعاً في اللوحة** لا سرعةً بالكيلومتر/ساعة.
// ─────────────────────────────────────────────────────────────────────────────
const WIND_PALETTE: ColorStop[] = [
    [0, [48, 68, 166, 1.0]],
    [1, [48, 72, 168, 1.0]],
    [2, [48, 75, 169, 1.0]],
    [3, [48, 78, 170, 1.0]],
    [4, [48, 82, 172, 1.0]],
    [5, [48, 94, 175, 1.0]],
    [6, [48, 105, 179, 1.0]],
    [7, [48, 117, 182, 1.0]],
    [8, [49, 128, 186, 1.0]],
    [9, [49, 140, 189, 1.0]],
    [10, [49, 151, 193, 1.0]],
    [11, [49, 163, 196, 1.0]],
    [12, [52, 165, 192, 1.0]],
    [13, [55, 166, 189, 1.0]],
    [14, [58, 168, 185, 1.0]],
    [15, [61, 170, 181, 1.0]],
    [16, [64, 172, 177, 1.0]],
    [17, [67, 173, 174, 1.0]],
    [18, [70, 175, 170, 1.0]],
    [19, [79, 176, 160, 1.0]],
    [20, [89, 178, 150, 1.0]],
    [21, [98, 179, 140, 1.0]],
    [22, [107, 181, 129, 1.0]],
    [23, [116, 182, 119, 1.0]],
    [24, [126, 184, 109, 1.0]],
    [25, [135, 185, 99, 1.0]],
    [26, [131, 183, 97, 1.0]],
    [27, [128, 181, 94, 1.0]],
    [28, [124, 179, 92, 1.0]],
    [29, [121, 178, 89, 1.0]],
    [30, [117, 176, 87, 1.0]],
    [31, [114, 174, 84, 1.0]],
    [32, [110, 172, 82, 1.0]],
    [33, [125, 177, 81, 1.0]],
    [34, [140, 182, 80, 1.0]],
    [35, [155, 187, 79, 1.0]],
    [36, [170, 192, 78, 1.0]],
    [37, [185, 198, 78, 1.0]],
    [38, [200, 203, 77, 1.0]],
    [39, [215, 208, 76, 1.0]],
    [40, [230, 213, 75, 1.0]],
    [41, [230, 206, 73, 1.0]],
    [42, [230, 199, 71, 1.0]],
    [43, [230, 192, 69, 1.0]],
    [44, [231, 186, 66, 1.0]],
    [45, [231, 179, 64, 1.0]],
    [46, [231, 172, 62, 1.0]],
    [47, [231, 165, 60, 1.0]],
    [48, [231, 159, 58, 1.0]],
    [49, [231, 154, 57, 1.0]],
    [50, [231, 148, 55, 1.0]],
    [51, [232, 142, 53, 1.0]],
    [52, [232, 136, 51, 1.0]],
    [53, [232, 131, 50, 1.0]],
    [54, [232, 125, 48, 1.0]],
    [55, [231, 117, 46, 1.0]],
    [56, [230, 109, 44, 1.0]],
    [57, [229, 101, 42, 1.0]],
    [58, [227, 92, 39, 1.0]],
    [59, [226, 84, 37, 1.0]],
    [60, [225, 76, 35, 1.0]],
    [61, [224, 68, 33, 1.0]],
    [62, [220, 63, 38, 1.0]],
    [63, [216, 59, 44, 1.0]],
    [64, [212, 54, 49, 1.0]],
    [65, [207, 49, 54, 1.0]],
    [66, [203, 44, 59, 1.0]],
    [67, [199, 40, 65, 1.0]],
    [68, [195, 35, 70, 1.0]],
    [69, [195, 36, 83, 1.0]],
    [70, [194, 37, 96, 1.0]],
    [71, [194, 38, 110, 1.0]],
    [72, [194, 38, 123, 1.0]],
    [73, [194, 39, 136, 1.0]],
    [74, [194, 40, 150, 1.0]],
    [75, [193, 41, 163, 1.0]],
    [76, [193, 42, 176, 1.0]],
    [77, [187, 43, 178, 1.0]],
    [78, [181, 43, 179, 1.0]],
    [79, [175, 44, 181, 1.0]],
    [80, [169, 44, 182, 1.0]],
    [81, [162, 45, 184, 1.0]],
    [82, [156, 46, 186, 1.0]],
    [83, [150, 46, 187, 1.0]],
    [84, [144, 47, 189, 1.0]],
    [85, [138, 47, 190, 1.0]],
    [86, [132, 48, 192, 1.0]],
    [87, [129, 54, 189, 1.0]],
    [88, [125, 60, 186, 1.0]],
    [89, [122, 65, 183, 1.0]],
    [90, [119, 71, 180, 1.0]],
    [91, [116, 77, 177, 1.0]],
    [92, [112, 83, 175, 1.0]],
    [93, [109, 89, 172, 1.0]],
    [94, [106, 95, 169, 1.0]],
    [95, [103, 100, 166, 1.0]],
    [96, [99, 106, 163, 1.0]],
    [97, [96, 112, 160, 1.0]],
    [98, [107, 117, 149, 1.0]],
    [99, [117, 123, 139, 1.0]],
    [100, [128, 128, 128, 1.0]],
];

/**
 * محاذاة تدرّج الرياح على نِسب Windy — **بلا تغيير أي لون**.
 *
 * الأرقام **مقيسة** من شريط المرجع، لا مقدَّرة بالعين. عُوين الشريط بكسلاً بكسل، وعُوير
 * المحور من الأرقام البيضاء المطبوعة عليه (كل رقم عند علامته)، فثبت أن العلامات متساوية
 * التباعد موضعياً — وهو ما يُصادق على الإطار قبل قراءة أي لون.
 *
 * النطاقات المقيسة (كم/س):
 *
 *     أزرق غامق   0.7 –  8.5        ماجنتا     63.0 – 75.6
 *     تركوازي     8.5 – 21.7        بنفسجي     75.6 – 83.4
 *     أخضر       21.7 – 35.0        أزرق غامق  83.4 – 93.8
 *     أصفر-أخضر  35.0 – 41.5        أزرق فاتح  93.8 – 97.7
 *     أصفر       41.5 – 46.7
 *     برتقالي    46.7 – 55.2
 *     أحمر       55.2 – 63.0
 *
 * **لماذا يُوثَق بهذه القراءة:** تؤكّدها ملاحظة مستقلّة تماماً — لقطتان لنفس النقطة على
 * نفس الحقل أظهرتا المرجع **برتقالياً عند 54 كم/س**، وهذه القراءة تضع البرتقالي في
 * 46.7–55.2. قراءة أولى كانت تضعه في 63.1–69.8 وتجعل 54 أخضر، فسقطت بهذا الاختبار.
 *
 * **مفاجأة الطرف الأعلى:** تدرّج المرجع لا ينتهي عند الماجنتا — يعود عبر البنفسجي إلى
 * الأزرق ثم الأزرق الفاتح قبل 100. ولوحتنا تملك هذا التسلسل نفسه (المواضع 72 ← 96)، لكنّه
 * كان محشوراً **فوق** 100 حيث لا يُرى أبداً. وهذا هو ما رُصد كـ«لا توجد لدينا ألوان تعادل
 * ما بعد 75 لديهم» — الألوان موجودة، وكان موضعها خطأً.
 *
 * **مقايضة مقصودة:** مطابقة الشريط تستهلك اللوحة كلّها تقريباً عند 100 كم/س، فلا يتبقّى
 * لما فوقها إلا المواضع 96–100 — أي أن 100–229.5 كم/س تكاد تكون بلون واحد. هذا ثمن
 * المطابقة الحرفية المطلوبة؛ ولو أردنا تمييزاً أوضح للعواصف الشديدة لوجب إبقاء احتياطي
 * أكبر وقبول انحراف الشريط عند طرفه.
 *
 * ملاحظة على الألوان لا النِسب: شريط المرجع **نصف شفّاف فوق الخريطة**، فألوانه المقروءة
 * مكتومة (‏rgb(162,133,65) حيث نضع نحن برتقالياً مشبعاً). لذلك نُقلت **مواضع حدوده** فقط،
 * وتبقى ألواننا كما هي كما طُلب صراحةً.
 *
 * كل زوج = (السرعة المعروضة، موضعها في WIND_PALETTE)، والمواضع من معالم لوحتنا الفعلية.
 */
const WIND_WINDY_ANCHORS: ReadonlyArray<readonly [number, number]> = [
    [0, 0], [8.5, 11], [21.7, 21], [35, 34], [41.5, 37], [46.7, 48], [55.2, 59],
    [63, 72], [75.6, 82], [83.4, 93], [93.8, 96], [229.5, 100],
];

/** يحوّل سرعة معروضة (كم/س) إلى موضع في WIND_PALETTE، خطّياً بين المعالم. */
function windSpeedToPalettePos(speed: number): number {
    const a = WIND_WINDY_ANCHORS;
    if (speed <= a[0][0]) return a[0][1];
    for (let i = 0; i < a.length - 1; i += 1) {
        const [s0, p0] = a[i];
        const [s1, p1] = a[i + 1];
        if (speed <= s1) return p0 + ((p1 - p0) * (speed - s0)) / (s1 - s0);
    }
    return a[a.length - 1][1];
}

/**
 * يبني جدول (سرعة → لون) بأخذ عيّنات اللوحة عبر المحاذاة أعلاه.
 * كثافة العيّنات: كل 1 كم/س حتى 100 (حيث يقع الحقل الحقيقي وحيث الشريط)، ثم كل 5 فوقها.
 */
function buildWindStops(): ColorStop[] {
    const out: ColorStop[] = [];
    for (let s = 0; s <= 100; s += 1) out.push([s, interpolateStops(windSpeedToPalettePos(s), WIND_PALETTE)]);
    for (let s = 105; s <= 230; s += 5) out.push([s, interpolateStops(windSpeedToPalettePos(s), WIND_PALETTE)]);
    return out;
}

// الجدول النهائي (سرعة كم/س ← لون) بعد المحاذاة على مواضع Windy — هذا ما يُستخدَم
// فعلياً في STOPS_REGISTRY، لا اللوحة الخام WIND_PALETTE أعلاه (تلك مدخل داخلي فقط).
const WIND_HEATMAP_STOPS: ColorStop[] = buildWindStops();

// ─────────────────────────────────────────────────────────────────────────────
// WIND GUSTS (هبات الرياح) — نفس تدرّج سرعة الرياح بالحرف الواحد (طلب توحيد صريح؛
// كانا يختلفان سابقاً)، لا نسخة منفصلة كي لا ينحرفا مجدداً.
// ─────────────────────────────────────────────────────────────────────────────
const WIND_GUST_HEATMAP_STOPS: ColorStop[] = WIND_HEATMAP_STOPS;

// (كل درجتين) على كامل مدى الواجهة، ما يلغي انحراف الاستيفاء الخطّي بين نقاط متباعدة
// (كانت منطقة 22–28° مثلاً أفتح/أصفر أكثر من المرجع). ألفا 1.0 (معتمة كـ Zoom Earth).
const TEMP_HEATMAP_STOPS: ColorStop[] = [
    [-60, [228, 205, 229, 1.0]],
    [-58, [226, 202, 228, 1.0]],
    [-56, [225, 201, 227, 1.0]],
    [-54, [224, 198, 226, 1.0]],
    [-52, [223, 197, 225, 1.0]],
    [-50, [221, 194, 224, 1.0]],
    [-48, [216, 185, 219, 1.0]],
    [-46, [210, 174, 215, 1.0]],
    [-44, [203, 165, 210, 1.0]],
    [-42, [198, 155, 206, 1.0]],
    [-40, [191, 145, 201, 1.0]],
    [-38, [183, 137, 199, 1.0]],
    [-36, [175, 128, 197, 1.0]],
    [-34, [166, 118, 195, 1.0]],
    [-32, [156, 109, 192, 1.0]],
    [-30, [142,  99, 185, 1.0]],
    [-28, [129,  88, 179, 1.0]],
    [-26, [113,  76, 171, 1.0]],
    [-24, [ 96,  62, 163, 1.0]],
    [-22, [ 77,  52, 154, 1.0]],
    [-20, [ 61,  49, 149, 1.0]],
    [-18, [ 54,  60, 153, 1.0]],
    [-16, [ 49,  70, 156, 1.0]],
    [-14, [ 52,  80, 161, 1.0]],
    [-12, [ 53,  89, 165, 1.0]],
    [-10, [ 56,  98, 170, 1.0]],
    [ -8, [ 57, 109, 178, 1.0]],
    [ -6, [ 59, 119, 185, 1.0]],
    [ -4, [ 61, 130, 194, 1.0]],
    [ -2, [ 66, 145, 196, 1.0]],
    [  0, [ 73, 160, 198, 1.0]],
    [  2, [ 73, 180, 201, 1.0]],
    [  4, [ 67, 197, 205, 1.0]],
    [  6, [ 74, 210, 201, 1.0]],
    [  8, [102, 218, 184, 1.0]],
    [ 10, [116, 223, 162, 1.0]],
    [ 12, [127, 227, 136, 1.0]],
    [ 14, [157, 234, 132, 1.0]],
    [ 16, [189, 241, 135, 1.0]],
    [ 18, [220, 245, 138, 1.0]],
    [ 20, [243, 245, 129, 1.0]],
    [ 22, [246, 229, 100, 1.0]],
    [ 24, [247, 209,  84, 1.0]],
    [ 26, [247, 186,  68, 1.0]],
    [ 28, [246, 161,  54, 1.0]],
    [ 30, [246, 136,  40, 1.0]],
    [ 32, [244, 113,  41, 1.0]],
    [ 34, [242,  90,  42, 1.0]],
    [ 36, [239,  67,  42, 1.0]],
    [ 38, [237,  45,  44, 1.0]],
    [ 40, [215,  37,  44, 1.0]],
    [ 42, [186,  29,  45, 1.0]],
    [ 44, [158,  24,  50, 1.0]],
    [ 46, [138,  17,  50, 1.0]],
    [ 48, [118,   9,  49, 1.0]],
    [ 50, [ 97,   0,  48, 1.0]],
    [ 52, [ 86,   0,  43, 1.0]],
    [ 54, [ 75,   0,  37, 1.0]],
    [ 56, [ 63,   0,  31, 1.0]],
    [ 58, [ 52,   0,  26, 1.0]],
];

// ─────────────────────────────────────────────────────────────────────────────
// WET-BULB (اللمبة الرطبة) — مستخرَج حرفياً من zoom.earth/maps/temperature-wet-bulb
// (صورة تدرّج PNG في CSS الشريط، معايَرة بأرقامه 0..40). المرجع الحقيقي: رمادي متدرّج
// للقيم الباردة (<~8°)، ثم أصفر ← برتقالي ← أحمر ← عنّابي شبه أسود عند 38+ (حدّ البقاء).
// ─────────────────────────────────────────────────────────────────────────────
const WETBULB_HEATMAP_STOPS: ColorStop[] = [
    [-42, [ 85,  85,  85, 1.0]],
    [-40, [ 90,  90,  90, 1.0]],
    [-36, [102, 102, 102, 1.0]],
    [-32, [115, 115, 115, 1.0]],
    [-28, [127, 127, 127, 1.0]],
    [-24, [139, 139, 139, 1.0]],
    [-20, [150, 150, 150, 1.0]],
    [-16, [163, 163, 163, 1.0]],
    [-12, [175, 175, 175, 1.0]],
    [ -8, [187, 187, 187, 1.0]],
    [ -4, [199, 199, 199, 1.0]],
    [  0, [211, 211, 211, 1.0]],
    [  2, [218, 218, 218, 1.0]],
    [  4, [224, 224, 224, 1.0]],
    [  6, [231, 231, 231, 1.0]],
    [  8, [238, 238, 238, 1.0]],
    [ 10, [243, 244, 216, 1.0]],
    [ 12, [246, 249, 183, 1.0]],
    [ 14, [249, 254, 151, 1.0]],
    [ 16, [247, 248, 125, 1.0]],
    [ 18, [247, 235, 105, 1.0]],
    [ 20, [247, 210,  86, 1.0]],
    [ 22, [248, 181,  66, 1.0]],
    [ 24, [246, 141,  43, 1.0]],
    [ 26, [242,  99,  41, 1.0]],
    [ 28, [238,  55,  43, 1.0]],
    [ 30, [216,  37,  43, 1.0]],
    [ 32, [177,  28,  46, 1.0]],
    [ 34, [130,  13,  50, 1.0]],
    [ 36, [ 81,   0,  40, 1.0]],
    [ 38, [ 38,   0,   5, 1.0]],
];

// ─────────────────────────────────────────────────────────────────────────────
// THUNDERSTORM INDICES — K-index و O-index (CAPE)
// K-index: رمادي/شفاف للقيم غير المهمة، ثم أصفر ← برتقالي ← أحمر ← أرجواني للقيم العالية،
// مثل خرائط كثافة/احتمالية العواصف في Windy/Meteologix.
//
// O-index = CAPE الخام (J/kg) — راجع derive_thunder_indices.py. العتبات والتدرّج هنا
// مطابقان لطبقة "Thunder" في Windy (تلوين ممتلئ شبه شفاف فوق كل اليابسة، لا رموز):
// <1000 ضعيف، 1000-2500 متوسط، 2500-4000 قوي، >4000 شديد (تصنيف أرصادي قياسي).
// ─────────────────────────────────────────────────────────────────────────────
const K_INDEX_STOPS: ColorStop[] = [
    [15, [150, 150, 150, 0.00]],
    [20, [185, 205,  75, 0.55]],
    [25, [236, 224,  35, 0.70]],
    [30, [247, 147,  30, 0.82]],
    [35, [218,  41,  61, 0.90]],
    [40, [142,  18,  92, 0.94]],
    [45, [ 88,  24, 126, 0.96]],
];

/*
 * كثافة الصواعق (l/km²) — **العتبات حرفياً من مفتاح Windy**: 0.025 / 0.1 / 1 / 10 / 20.
 *
 * الكمّية تغيّرت من CAPE (J/kg، 0..5000) إلى كثافة صواعق مقدَّرة (l/km²، 0..20) بعد أن
 * أثبت الفحص أن طبقة Windy تعرض كثافة ومضات لا طاقة — راجع derive_thunder_indices.py.
 *
 * السلّم لوغاريتمي فعلياً (0.025 → 20 أي ٨٠٠ ضعفاً على خمس عتبات)، وهذا مقصود: نشاط
 * الصواعق يتوزّع لوغاريتمياً، فسلّم خطّي يجعل كل شيء دون أعلى عتبة بلون واحد باهت.
 *
 * الشفافية عند 0.025 صفر تماماً: دون هذه العتبة لا نشاط يستحقّ التلوين، وهي ما يمنح
 * الطبقة خلفيتها الرمادية النظيفة كما في Windy بدل الطلاء الشامل.
 */
const O_INDEX_STOPS: ColorStop[] = [
    [ 0.000, [150, 150, 150, 0.00]],
    [ 0.025, [120, 200,  70, 0.55]],   // أخضر
    [ 0.100, [230, 225,  55, 0.72]],   // أصفر
    [ 1.000, [245, 150,  35, 0.85]],   // برتقالي
    [10.000, [230,  45,  50, 0.92]],   // أحمر
    [20.000, [225,  60, 190, 0.96]],   // ماجنتا — قمّة مفتاح Windy
];

// ─────────────────────────────────────────────────────────────────────────────
// PRECIPITATION (هطول الأمطار) — عتبات القيم (mm/h) مطابقة حرفياً لمفتاح ألوان Windy
// (1.5 / 2 / 3 / 7 / 10 / 20 / 30 — راجع لقطة legend الرسمية)، لكن الألوان نفسها
// مسحوبة **حرفياً** من WIND_PALETTE أعلاه (نفس الأزرق عند البداية ونفس الأحمر/
// الأرجواني عند الذروة) بدل نسخ ألوان Windy — طلب صريح: "النسب كما Windy، الألوان كألواننا".
// يبدأ التدرّج من الصفر الحقيقي. الصفر نفسه شفاف، وأي قيمة موجبة تظهر تدريجياً؛ لذلك
// لا توجد فجوة سوداء بين 0.0 مم وأول لون مرئي في الخريطة.
// 0.3/0.8 عتبتان انتقاليتان قبل أول علامة Windy (1.5) لتفادي قفزة مفاجئة من الشفافية
// إلى أزرق كامل عند أمطار خفيفة جداً — تدرّج القيم بعدهما يتبع تسلسل ألوان الرياح
// (أزرق ← سماوي ← أخضر ← أصفر ← برتقالي ← أحمر ← أرجواني) بنفس ترتيبه هناك تماماً.
//
// **منحنى الشفافية عند الطرف الخفيف** هو ما يفصل بين "لطخة مائية" و"نقاط":
// كان 0.1 مم يُرسم بشفافية 0.38 و0.5 مم بـ 0.72، أي أن خليّة راستر واحدة فيها أثر رذاذ
// (0.2 مم/٣ س) تُرسم بعتامة ~0.44 — بقعة زرقاء صريحة على قاعدة رمادية. وهذا الأثر
// منتشر على المحيطات في كل إطار تقريباً، فتظهر الخريطة منقّطة بحبيبات معزولة بحجم خليّة
// الشبكة الواحدة بدل حقل متّصل — وهو بالضبط ما رُصد مقارنةً بـ Windy. المنحنى الآن
// يمتدّ: أثر الرذاذ حضور بالكاد مرئي (0.07)، والعتامة الكاملة لا تُبلغ قبل 3 مم، فتذوب
// الحبيبات المعزولة في تدرّج ناعم وتبقى الأنوية القويّة صريحة كما هي. الألوان نفسها لم
// تتغيّر بحرف — الشفافية وحدها.
//
// 40/50 امتداد لما بعد آخر علامة في مفتاح Windy (30): مفتاح Windy المعروض يتوقّف
// عند "30" لكن خريطته الفعلية **لا تتجمّد** عند هذا اللون — الشدّات القصوى (>30 مم/س)
// تستمرّ بألوان أعمق تدريجياً (نفس مبدأ "القيمة الأعلى = تمييز أوضح"). بدون هذا
// الامتداد كانت كل قيمة فوق 30 (37، 51، ...) تُرسَم بنفس لون 30 تماماً — تسطيح
// غير موجود لدى Windy. الامتداد يواصل نفس تسلسل ألوان الرياح (مواضع أعمق أرجوانياً/
// بنفسجياً) لا لوناً جديداً مخترَعاً. سقف نطاق القيم [0,50] في weatherTextures.ts
// (VALUE_RANGES) يبقى كما هو — 50 هو آخر عتبة نافعة فعلياً.
//
// 2.0/3.0/7.0 مُبقاة على درجات أزرق/تركواز أعمق (مواضع 15/19/35 بدل 18/22/38 سابقاً):
// عند الشدّة الخفيفة-المتوسطة يبقى الطابع "مائياً" (أزرق/تركوازياً) لفترة أطول قبل
// الانتقال إلى الأصفر/الأخضر — بلا اختراع لون جديد، مجرّد مواضع أعمق في نفس لوحة الرياح.
//
// 50.0 يُنهي التدرّج بلون فاتح شبه أبيض (مشبَّع بصبغة أرجوانية خفيفة من نفس نهاية
// اللوحة) بدل توقّفه عند بنفسجي غامق: الشدّة القصوى في المرجع تُقرأ فاتحة القلب
// (شبه بيضاء) لا داكنة — والقلب الفاتح هنا مُشتقّ بتفتيح آخر لون في لوحتنا (WIND_PALETTE[85])
// نحو الأبيض، لا بنسخ لون بعينه من أي مرجع خارجي.
// ─────────────────────────────────────────────────────────────────────────────
const PRECIP_HEATMAP_STOPS: ColorStop[] = [
    [ 0.0, [131, 197, 248, 0.0]],  // لا هطول = شفاف تماماً
    [ 0.3, [131, 197, 248, 0.07]], // أثر رذاذ — حضور بالكاد مرئي، لا نقطة صريحة
    [ 0.8, [ 91, 156, 242, 0.20]], // أزرق أعمق قليلاً — لون انتقالي أصلي
    [ 1.5, [ 49, 140, 189, 0.52]], // = WIND_PALETTE[9]  (أزرق) — علامة Windy الأولى
    [ 2.0, [ 61, 170, 181, 0.74]], // = WIND_PALETTE[15] (أزرق-تركواز أعمق) — طابع مائي أطول
    [ 3.0, [ 79, 176, 160, 1.0]],  // = WIND_PALETTE[19] (تركواز-أخضر) — أوّل عتامة كاملة
    [ 7.0, [170, 192,  78, 1.0]],  // = WIND_PALETTE[35] (أصفر-أخضر)
    [10.0, [231, 159,  58, 1.0]],  // = WIND_PALETTE[48] (برتقالي)
    [20.0, [225,  76,  35, 1.0]],  // = WIND_PALETTE[60] (برتقالي محمرّ)
    [30.0, [193,  41, 163, 1.0]],  // = WIND_PALETTE[75] (أرجواني/قرمزي) — علامة Windy الأخيرة
    [40.0, [169,  44, 182, 1.0]],  // = WIND_PALETTE[80] (بنفسجي أعمق) — امتداد ما بعد Windy
    [50.0, [224, 205, 236, 1.0]],  // WIND_PALETTE[85] مُفتَّح نحو الأبيض — قلب الشدّة القصوى
];

// ─────────────────────────────────────────────────────────────────────────────
// PRESSURE (الضغط الجوي) — **عتبات Windy، ولونٌ مصمَّم في فضاء OKLCH.**
//
// خمس محاولات سبقت هذه، وكل واحدة أسقطت فرضيّة:
//
//  ١. سلّم مكتوب بالنظر إلى لقطات — باهت (‏1000 hPa أزرق رمادي شاحب)، وعتامته 0.6.
//  ٢. سلّم Zoom Earth مستخرَجاً من نسيج palette عندهم — صحيح وجميل، لكن **تعادله
//     أبيض ناصع** فابتلع طبقة الأيزوبار في المدارات وشبه المدارات. قيس بالمسح:
//     824 منحنى محسوباً و456 مرسوماً، والخريطة تبدو خالية من الخطوط هناك.
//  ٣. سلّم Windy حرفياً — أصلح الأيزوبار (تعادله رماديّ) لكنه مكتوم التشبّع.
//  ٤. سلّم Windy برفع تشبّع ميكانيكي (‏×1.55 في HLS) — أحيا الطرفين، وبقي نطاق
//     1015–1030 كاكيّاً طينياً: رفعُ التشبّع لا يُصلح صبغةً واقعةً أصلاً في الزيتوني.
//  ٥. هذه: العتبات تبقى عتباتهم، واللون **يُصمَّم** لا يُحوَّل.
//
// ── ما الذي يجعلها «عصرية» بمعنى قابل للقياس ─────────────────────────────────
//
// بُنيت في **OKLCH** (فضاء إدراكيّ موحّد) بثلاث خصائص مضبوطة صراحةً:
//
//  • **السطوع رتيب** من الطرفين نحو الوسط: 0.32 → 0.862 → 0.375. فالتدرّج يُقرأ
//    كتلٌّ واحد صاعد هابط، ولا تظهر فيه «حزم» كاذبة كالتي تُنتجها القفزات في RGB.
//  • **التشبّع يتلاشى إلى الصفر عند التعادل** (‏C: 0.13 → 0.010 → 0.14). وهذا هو
//    تعريف التدرّج المتباعد السليم: الصفر لا لون له، والاتجاهان متكافئان.
//  • **الصبغة نظيفة على الجانبين**: 268°→195° بارداً (نيليّ ← أزرق ← سماوي)، و85°→2°
//    دافئاً (رمليّ ← كهرمانيّ ← برتقالي ← أحمر ← خمريّ). والكاكي الطينيّ عند Windy
//    ينشأ من صبغة ~40° بتشبّع منخفض؛ رفعُ التشبّع هناك يزيده اصفراراً، أمّا نقل
//    الصبغة إلى 62–78° مع تشبّع أعلى فيُعطي كهرماناً نظيفاً بنفس موضع القراءة.
//
// ── ما لم يتغيّر، وهو الأهمّ ─────────────────────────────────────────────────
//
// **العتبات كلّها من Windy حرفاً**: 955، 976، 986، 995، 1002، 1007، 1011.25،
// 1013.25، 1015.25، 1019، 1024، 1030، 1038، 1046 — مأخوذة من تعريفها
// (‏`ident:'pressure', steps:4000`، قيَمه بالباسكال). فهي ما يحدّد **أين** يتغيّر
// اللون، أي قراءة الخريطة نفسها؛ واللون كسوةٌ عليها.
//
// **والتعادل يبقى رماديّاً ‎[205,210,216] لا أبيض.** هذا شرط عمل طبقة الأيزوبار
// فوق أوسع رقعة على الخريطة، وقد كُلّفنا ثلاث محاولات لنعرفه. اختُبر بالمعاينة:
// رُسمت خطوط الأيزوبار بألوانها الفعلية فوق الشريط كلّه فبقيت مقروءة في كل نطاق.
// أي تفتيح لهذه النقطة يُعيد العطب.
// ─────────────────────────────────────────────────────────────────────────────
const PRESSURE_HEATMAP_STOPS: ColorStop[] = [
    [    955, [ 24,  41, 116, 1.0]],   // نيليّ عميق — منخفض شديد
    [    976, [  5,  75, 162, 1.0]],
    [    986, [  0, 114, 184, 1.0]],
    [    995, [  0, 154, 187, 1.0]],
    [   1002, [ 63, 184, 190, 1.0]],   // سماويّ
    [   1007, [128, 204, 203, 1.0]],
    [1011.25, [168, 214, 216, 1.0]],
    [1013.25, [205, 210, 216, 1.0]],   // ← التعادل: رماديّ بارد — يفسح لخطوط الأيزوبار
    [1015.25, [219, 202, 169, 1.0]],
    [   1019, [221, 183, 124, 1.0]],   // رمليّ نظيف (لا كاكي)
    [   1024, [225, 148,  73, 1.0]],   // كهرمانيّ
    [   1030, [218, 108,  52, 1.0]],   // برتقالي
    [   1038, [196,  60,  45, 1.0]],
    [   1046, [154,  20,  55, 1.0]],
    [   1050, [119,  14,  59, 1.0]],   // خمريّ داكن — مرتفع شديد
];

// ─────────────────────────────────────────────────────────────────────────────
// HUMIDITY (الرطوبة) — مستخرَج حرفياً من zoom.earth/maps/humidity (صورة تدرّج PNG
// في CSS الشريط، معايَرة بأرقامه 0..100): بنّي (جاف) ← أصفر كريمي (~50) ← أخضر
// نعناعي ← تركواز ← أزرق رمادي معتم عند التشبّع. معتمة تماماً كـ Zoom Earth.
// ─────────────────────────────────────────────────────────────────────────────
const HUMIDITY_HEATMAP_STOPS: ColorStop[] = [
    [  0, [135,  75,  41, 1.0]],
    [  5, [163,  98,  55, 1.0]],
    [ 10, [192, 121,  69, 1.0]],
    [ 15, [213, 145,  82, 1.0]],
    [ 20, [222, 167,  92, 1.0]],
    [ 25, [232, 189, 101, 1.0]],
    [ 30, [240, 208, 113, 1.0]],
    [ 35, [243, 218, 126, 1.0]],
    [ 40, [246, 227, 139, 1.0]],
    [ 45, [250, 241, 156, 1.0]],
    [ 50, [251, 255, 175, 1.0]],
    [ 55, [197, 254, 193, 1.0]],
    [ 60, [182, 250, 191, 1.0]],
    [ 65, [166, 242, 194, 1.0]],
    [ 70, [148, 233, 199, 1.0]],
    [ 75, [129, 222, 203, 1.0]],
    [ 80, [101, 201, 203, 1.0]],
    [ 85, [ 77, 182, 201, 1.0]],
    [ 90, [ 62, 164, 192, 1.0]],
    [ 95, [ 51, 139, 173, 1.0]],
    [100, [ 47, 110, 157, 1.0]],
];

// ─────────────────────────────────────────────────────────────────────────────
// DEW POINT (نقطة الندى) — مستخرَج حرفياً من صورة تدرّج Zoom Earth (كل درجتين):
// بنّي غامق (جاف جداً) ← كهرماني ← أصفر فاتح (~12) ← أخضر/تركواز ← نيلي شبه أسود (رطب جداً).
// معتمة تماماً كـ Zoom Earth.
// ─────────────────────────────────────────────────────────────────────────────
const DEWPOINT_HEATMAP_STOPS: ColorStop[] = [
    [-36, [102,  61,  30, 1.0]],
    [-34, [106,  64,  32, 1.0]],
    [-32, [110,  66,  33, 1.0]],
    [-30, [114,  68,  34, 1.0]],
    [-28, [117,  71,  35, 1.0]],
    [-26, [121,  73,  36, 1.0]],
    [-24, [125,  75,  37, 1.0]],
    [-22, [129,  78,  39, 1.0]],
    [-20, [134,  81,  40, 1.0]],
    [-18, [144,  89,  46, 1.0]],
    [-16, [154,  97,  51, 1.0]],
    [-14, [165, 105,  56, 1.0]],
    [-12, [174, 113,  61, 1.0]],
    [-10, [185, 121,  66, 1.0]],
    [ -8, [195, 129,  71, 1.0]],
    [ -6, [204, 137,  76, 1.0]],
    [ -4, [212, 147,  82, 1.0]],
    [ -2, [218, 159,  87, 1.0]],
    [  0, [225, 172,  95, 1.0]],
    [  2, [231, 184, 100, 1.0]],
    [  4, [237, 196, 106, 1.0]],
    [  6, [241, 208, 118, 1.0]],
    [  8, [244, 220, 132, 1.0]],
    [ 10, [249, 234, 146, 1.0]],
    [ 12, [253, 250, 161, 1.0]],
    [ 14, [203, 255, 187, 1.0]],
    [ 16, [163, 247, 201, 1.0]],
    [ 18, [122, 215, 204, 1.0]],
    [ 20, [ 71, 175, 197, 1.0]],
    [ 22, [ 53, 142, 176, 1.0]],
    [ 24, [ 47, 108, 157, 1.0]],
    [ 26, [ 33,  83, 139, 1.0]],
    [ 28, [ 25,  64, 115, 1.0]],
    [ 30, [ 20,  50,  89, 1.0]],
    [ 32, [ 14,  36,  65, 1.0]],
    [ 34, [  8,  21,  39, 1.0]],
];

// ─────────────────────────────────────────────────────────────────────────────
// CLOUDS (الغيوم) — **منقول من صيغة Zoom Earth نفسها، لا من لقطة شاشة.**
//
// غيومهم ليست تدرّجاً لونياً إطلاقاً. في شيدر طبقة الهطول عندهم تُخزَّن التغطية في
// القناة الحمراء (مدى 0..100%)، ثم يُركَّب لونها هكذا:
//
//     اللون   = vec3(0.95)          → أي rgb(242,242,242) ثابت لا يتغيّر
//     الشفافية = clouds * a*a*a*a    → أي (التغطية)⁴
//
// ── لماذا الأُسّ الرابع هو بيت القصيد ────────────────────────────────────────
//
// سلّمنا السابق كان خطّياً تقريباً: 40% تغطية تعطي شفافية 0.42. والغطاء الجزئي
// شائع جداً على الخريطة — معظم الكوكب في أي لحظة بين 20% و60% — فكانت النتيجة
// **ضباباً رمادياً يعمّ كل شيء** ولا يميّز غيمةً من صفاء. والأُسّ الرابع يعطي عند
// 40% شفافيةَ 0.026 (لا شيء تقريباً) وعند 90% شفافيةَ 0.66: فتختفي الغيوم المتفرّقة
// وتبقى الكتل الحقيقية وحدها. وهذا فيزيائياً أصدق أيضاً: ما يراه القمر الصناعي أبيضَ
// ناصعاً هو الغطاء الكثيف المتّصل، لا نسبةٌ إحصائية موزّعة على خليّة.
//
// ── ولماذا لونٌ واحد لا تدرّج ────────────────────────────────────────────────
// التدرّج القديم كان يبدأ مزرقّاً ‎[200,220,240] وينتهي أبيض. والزرقة عند التغطية
// المنخفضة تُقرأ «سماء» لا «غيمة»، فتزاحم لون البحر تحتها. اللون الثابت يجعل
// **الكثافة وحدها** هي ما يتغيّر، وهي المعلومة الوحيدة في الحقل أصلاً.
//
// النقاط الإحدى عشرة تقارِب المنحنى الرباعي بخطأ أقصاه 1.6 من 255 — غير مرئي.
// وتباعدها غير منتظم: المنحنى شبه مسطّح تحت 50% وشديد الانحدار فوق 85%.
//
// ملاحظة لازمة: عتامة الطبقة نفسها يجب أن تبقى 1، وإلا ضُربت هذه الأرقام فيها
// فانهار الطرف الأعلى (كانت 0.5 فصار الغطاء الكامل 0.45 بدل 1).
// ─────────────────────────────────────────────────────────────────────────────
const CLOUD_HEATMAP_STOPS: ColorStop[] = [
    [  0, [242, 242, 242, 0.0000]],
    [ 20, [242, 242, 242, 0.0016]],
    [ 35, [242, 242, 242, 0.0150]],
    [ 50, [242, 242, 242, 0.0625]],
    [ 60, [242, 242, 242, 0.1296]],
    [ 70, [242, 242, 242, 0.2401]],
    [ 78, [242, 242, 242, 0.3702]],
    [ 85, [242, 242, 242, 0.5220]],
    [ 90, [242, 242, 242, 0.6561]],
    [ 95, [242, 242, 242, 0.8145]],
    [100, [242, 242, 242, 1.0000]],
];

// ─────────────────────────────────────────────────────────────────────────────
// SST ANOMALY (شذوذ حرارة سطح البحر — النينيو/النينيا)
//
// **تدرّج متباعد حول الصفر** لا أحادي الاتجاه كبقيّة الطبقات: الصفر هنا ليس أدنى
// القيم بل «الوضع الطبيعي»، والاتجاهان (أدفأ/أبرد) متكافئان. لذلك يجب أن يبقى
// التماثل حول 0 مضبوطاً — أي إزاحة تجعل الخريطة توحي بدفء أو برد ليس في البيانات.
//
// المدى ±5 °م يطابق VMIN/VMAX في oisst_to_raster.py وVALUE_RANGES['sst-anomaly'].
// ±4 كان يكفي بصرياً لكن شذوذ Niño 1+2 بلغ +4.0 فعلاً في أغسطس 2026، وقصّه إلى لون
// القمّة كان سيخفي ذروة الحدث نفسه الذي بُنيت الطبقة لعرضه.
//
// ── الصفر **أبيض معتم**، والتدرّج كلّه معتم ────────────────────────────────────
//
// كانت النافذة المحايدة (−0.2..+0.2) شفّافة تماماً بمنطق «أظهِر الخريطة تحتها».
// وكان ذلك خطأً لسببين:
//
//  ١. **يكذب على المفتاح.** شريط المفتاح يُبنى من هذه النقاط نفسها، فالشفافية تجعله
//     يعرض لون اللوحة الداكنة عند الصفر — أي أن الشريط يقول «الطبيعي أسود» بينما
//     المقصود «الطبيعي محايد». ومن يقرأ الشريط يبني عليه قراءته للخريطة كلّها.
//
//  ٢. **يخالف كل المراجع.** ‏Copernicus وNOAA وNASA تضع الأبيض عند الصفر: الأبيض
//     يقرأه البصر «لا شذوذ» فوراً، بينما الفراغ الداكن يُقرأ «لا بيانات». والفرق
//     بين المعنيين هو كامل رسالة هذه الخريطة.
//
// ولذلك أيضاً بقيت قاعدة الخريطة تحت هذه الطبقة مهمّة رغم إعتامها: اليابسة لا
// يغطّيها الحقل إطلاقاً (يُخفيها قناع البرّ)، فلونها من القاعدة وحدها.
// ─────────────────────────────────────────────────────────────────────────────
const SST_ANOMALY_STOPS: ColorStop[] = [
    [-5.0, [  8,  20,  60, 1]],
    [-4.0, [ 16,  42, 110, 1]],
    [-3.0, [ 30,  85, 175, 1]],
    [-2.0, [ 70, 140, 215, 1]],
    [-1.0, [150, 200, 240, 1]],
    [-0.4, [225, 240, 250, 1]],
    [ 0.0, [255, 255, 255, 1]],   // ← «لا شذوذ»
    [ 0.4, [252, 232, 224, 1]],
    [ 1.0, [244, 165, 130, 1]],
    [ 2.0, [214,  70,  60, 1]],
    [ 3.0, [165,  20,  35, 1]],
    [ 4.0, [110,   5,  25, 1]],
    [ 5.0, [ 70,   0,  18, 1]],
];

// ─────────────────────────────────────────────────────────────────────────────
// T850 ANOMALY (شذوذ حرارة 850 هكتوباسكال)
//
// **منقول حرفياً من مفتاح Tropical Tidbits** — قِيس بأخذ عيّنة بكسل من كل حزمة في
// شريط مفتاحه، لا بتقدير بصري. السبب أن الطبقة نفسها معروفة عند قرّائها بهذا السلّم
// بعينه: من يقرأ خرائط 850 يعرف «البنفسجي = اقتحام قطبي» و«القرمزي الغامق = موجة
// حرّ» من الشكل قبل الرقم. سلّمٌ أجمل لكنه مختلف كان سيُلغي تلك المعرفة المكتسبة.
//
// ── حزمٌ منفصلة لا تدرّج متّصل ───────────────────────────────────────────────
//
// بقيّة طبقات الموقع متدرّجة متّصلة، وهذه ليست كذلك عمداً: المفتاح مقروء بالعتبات
// (‏+6، +8، +10…) والحدّ الحادّ بين حزمتين هو ما يجعل «خطّ الشذوذ» قابلاً للتتبّع على
// الخريطة. التدرّج الناعم يذيب تلك الحدود فتضيع قابلية القراءة الكمّية.
//
// تُبنى الحزم من الجدول أدناه: لكل حزمة نقطتان بلونها نفسه (بدايتها وقبيل نهايتها)،
// فيبقى اللون ثابتاً داخلها ويقفز عند الحدّ. الفجوة EDGE (جزء من ألف) هي عرض القفزة —
// أضيق من تكميم الراستر نفسه (‎60/255 ≈ 0.24 °م) فلا تُرى إطلاقاً.
//
// ── لماذا السلّم غير رتيب في اللمعان ────────────────────────────────────────
// الحزمة 6..7 أغمق ممّا يليها (7..8)، وكذلك 12..14 أفتح ممّا يليها. هذا في المصدر
// نفسه لا خطأ نسخ — تكرار الأطياف مقصود عنده ليفرّق الشذوذ المعتدل عن المتطرّف بتغيّر
// الطيف (أحمر ← وردي) لا بالعتمة وحدها. أي «تصحيح» له يكسر التطابق الذي بُني لأجله.
// ─────────────────────────────────────────────────────────────────────────────

/** [الحدّ الأدنى للحزمة، اللون] — تصاعدياً؛ آخر حزمة تمتدّ حتى T850_ANOMALY_MAX. */
const T850_ANOMALY_BANDS: Array<[number, [number, number, number]]> = [
    [-30,  [255, 205, 186]],   // ما دون ‎-28 (حزمة مفتوحة الطرف في المصدر)
    [-28,  [248, 186, 197]],
    [-24,  [243, 167, 210]],
    [-20,  [235, 149, 221]],
    [-18,  [230, 132, 232]],
    [-16,  [223, 113, 244]],
    [-14,  [182,  91, 236]],
    [-12,  [145,  87, 219]],
    [-10,  [108,  82, 202]],
    [ -8,  [ 73,  78, 184]],
    [ -7,  [ 35,  76, 164]],
    [ -6,  [  2,  71, 147]],
    [ -5,  [ 19,  94, 173]],
    [ -4,  [ 41, 116, 197]],
    [ -3,  [ 59, 138, 223]],
    [ -2.5,[100, 183, 248]],
    [ -2,  [120, 205, 247]],
    [ -1.5,[140, 227, 246]],
    [ -1,  [159, 248, 244]],
    [ -0.5,[255, 255, 255]],   // ← «لا شذوذ»: ‎-0.5..+0.5 أبيض في المصدر
    [  0.5,[255, 254, 190]],
    [  1,  [254, 234, 160]],
    [  1.5,[253, 210, 131]],
    [  2,  [253, 179, 101]],
    [  2.5,[248, 139,  81]],
    [  3,  [239,  99,  62]],
    [  4,  [221,  61,  45]],
    [  5,  [194,  28,  38]],
    [  6,  [157,  37,  57]],
    [  7,  [186,  67,  85]],
    [  8,  [217,  96, 115]],
    [ 10,  [241, 134, 168]],
    [ 12,  [253, 167, 221]],
    [ 14,  [228, 151, 192]],
    [ 16,  [203, 134, 164]],
    [ 18,  [178, 118, 136]],
    [ 20,  [153, 102, 108]],
    [ 24,  [128,  85,  78]],
    [ 28,  [103,  70,  48]],   // ما فوق ‎+28
];

/** طرفا مدى الترميز — يطابقان vmin/vmax في gfs_to_raster.py وVALUE_RANGES. */
const T850_ANOMALY_MAX = 30;
const T850_BAND_EDGE = 0.001;

const T850_ANOMALY_STOPS: ColorStop[] = T850_ANOMALY_BANDS.flatMap(([lo, rgb], i) => {
    const hi = T850_ANOMALY_BANDS[i + 1]?.[0] ?? T850_ANOMALY_MAX;
    const c: [number, number, number, number] = [rgb[0], rgb[1], rgb[2], 1];
    return [[lo, c], [hi - T850_BAND_EDGE, c]] as ColorStop[];
});

// ─────────────────────────────────────────────────────────────────────────────
// السجلّ القابل للتعديل — الافتراضيات أعلاه + إمكانية الحقن من إعدادات الأدمن
// ─────────────────────────────────────────────────────────────────────────────

/**
 * الرادار المتوقَّع يُلوَّن **بتدرّج الرادار المرصود نفسه** (‏radarPalette.json، مفهرس
 * بـdBZ): الكمّية واحدة بوحدتها، فلونٌ واحد للقيمة الواحدة يجعل التوقّع والرصد
 * متقارنَين بالعين مباشرةً. ونسخ الجدول هنا كان سيفتح باب انحرافهما بصمت.
 */
const RADAR_SIM_STOPS: ColorStop[] = radarPalette.stops as ColorStop[];

export const DEFAULT_COLOR_STOPS: Record<ForecastGridType, ColorStop[]> = {
    wind: WIND_HEATMAP_STOPS,
    'radar-sim': RADAR_SIM_STOPS,
    'wind-gusts': WIND_GUST_HEATMAP_STOPS,
    temperature: TEMP_HEATMAP_STOPS,
    // Zoom Earth يستخدم نفس طيف درجة الحرارة للإحساس الحراري بالضبط
    'feels-like': TEMP_HEATMAP_STOPS,
    'wet-bulb': WETBULB_HEATMAP_STOPS,
    'k-index': K_INDEX_STOPS,
    'o-index': O_INDEX_STOPS,
    precipitation: PRECIP_HEATMAP_STOPS,
    pressure: PRESSURE_HEATMAP_STOPS,
    humidity: HUMIDITY_HEATMAP_STOPS,
    dewpoint: DEWPOINT_HEATMAP_STOPS,
    clouds: CLOUD_HEATMAP_STOPS,
    't850-anomaly': T850_ANOMALY_STOPS,
    'sst-anomaly': SST_ANOMALY_STOPS,
    // نفس التدرّج عمداً: الكمّية نفسها بوحدتها نفسها، والفرق مرجعُها لا سلّمها.
    'sst-anomaly-rel': SST_ANOMALY_STOPS,
};

// الطبقات التي تُخفى قيمها تحت أوّل نقطة (خلفية شفافة بدل لون أدنى نقطة)
const HIDE_BELOW_FIRST_STOP: ReadonlySet<ForecastGridType> = new Set([
    'k-index', 'o-index', 'precipitation', 'clouds', 'radar-sim',
]);

const STOPS_REGISTRY: Record<ForecastGridType, ColorStop[]> = { ...DEFAULT_COLOR_STOPS };

// يزداد عند كل تعديل — تستخدمه خامات WebGL لإبطال الكاش عند تغيير الألوان.
let colorScaleVersion = 0;

export function getColorScaleVersion(): number {
    return colorScaleVersion;
}

export function getColorStops(type: ForecastGridType): ColorStop[] {
    return STOPS_REGISTRY[type] ?? [];
}

/** حقن نقاط تدرّج جديدة (من إعدادات الأدمن). تُفرَز وتُقيَّد قيم rgba تلقائياً. */
export function setColorStops(type: ForecastGridType, stops: ColorStop[]): void {
    if (!Array.isArray(stops) || stops.length === 0) return;
    const sanitized: ColorStop[] = stops
        .filter((s): s is ColorStop => Array.isArray(s) && typeof s[0] === 'number' && Array.isArray(s[1]) && s[1].length === 4)
        .map(([v, [r, g, b, a]]) => [v, [
            Math.max(0, Math.min(255, Math.round(r))),
            Math.max(0, Math.min(255, Math.round(g))),
            Math.max(0, Math.min(255, Math.round(b))),
            clamp01(a),
        ]] as ColorStop)
        .sort((a, b) => a[0] - b[0]);
    if (sanitized.length === 0) return;

    /*
     * ── حارس وحدات: تدرّج من عصر سابق للطبقة يُغرِق الخريطة بلون واحد ──────────────
     *
     * حدث فعلاً في الإنتاج: طبقة «العواصف الرعدية» (o-index) كانت مؤشّراً مطبَّعاً على
     * 0–100، ثم صارت CAPE الخام بوحدة J/kg على 0–5000. الكود حُدِّث، لكن التدرّج المحفوظ
     * في لوحة الأدمن بقي على السلّم القديم (آخر نقطة 100). ولأن إعدادات الأدمن **تتجاوز**
     * افتراضيات الكود، صارت كل قيمة CAPE حقيقية (مئات إلى آلاف) أعلى من آخر نقطة، فتُقصّ
     * إلى لون القمّة: **الخريطة كلّها بنفسجية**، والمفتاح نفسه بنفسجي من طرفه إلى طرفه.
     *
     * والعطل صامت تماماً: لا خطأ في الكونسول، ولا فرق في الشبكة، ولا شيء يشير إلى أن
     * السبب تدرّج قديم — يبدو كأن البيانات نفسها فاسدة.
     *
     * الشرط هنا ليس تخميناً في نيّة المصمّم: إن كانت **كل** النقاط تقع في أدنى 5% من مدى
     * الترميز، فالتدرّج لا يستطيع تلوين شيء بمعنى — سيصبغ ما فوق 5% بلون واحد، أي عملياً
     * كل الخريطة. هذه حالة لا يقصدها أحد، فنرفضها ونُبقي افتراضيات الكود ونُعلن السبب.
     */
    const range = VALUE_RANGES[type];
    if (range) {
        const [vmin, vmax] = range;
        const span = vmax - vmin;
        const first = sanitized[0][0];
        const top = sanitized[sanitized.length - 1][0];
        // كلا الاتجاهين عطل حقيقي وقع فعلاً: تدرّج 0–100 على طبقة مداها 0–5000 صبغ
        // الخريطة كلّها بلون القمّة، والعكس (تدرّج يبدأ فوق المدى) يُخفي الطبقة تماماً.
        const crammedLow = span > 0 && top < vmin + span * 0.05;
        const startsAboveRange = first >= vmax;
        if (crammedLow || startsAboveRange) {
            console.warn(
                `[colorScales] تجاهُل تدرّج «${type}»: نقاطه ${first}..${top} بينما مدى الطبقة `
                + `${vmin}..${vmax} — تدرّج من وحدة قديمة `
                + `${crammedLow ? 'يُلوّن الخريطة بلون واحد' : 'يُخفي الطبقة كلّها'}. `
                + `صحّح التدرّج في لوحة الأدمن أو احذفه ليعود افتراضي الكود.`,
            );
            return;
        }
    }

    STOPS_REGISTRY[type] = sanitized;
    colorScaleVersion += 1;
}

export function resetColorStops(type: ForecastGridType): void {
    STOPS_REGISTRY[type] = DEFAULT_COLOR_STOPS[type];
    colorScaleVersion += 1;
}

/**
 * تحويل نقاط التدرّج إلى linear-gradient CSS (لشريط المفتاح ومعاينة الداشبورد).
 * min/max اختياريان لقصّ النافذة المعروضة (مثلاً شريط الحرارة −40..50 بينما
 * النقاط تغطي −60..58) — بدونهما تُستخدم كامل مدى النقاط.
 */
export function stopsToCssGradient(stops: ColorStop[], min?: number, max?: number): string {
    if (!stops || stops.length === 0) return 'transparent';
    if (stops.length === 1) return toRgba(stops[0][1]);
    const lo = min ?? stops[0][0];
    const hi = max ?? stops[stops.length - 1][0];
    const span = hi - lo || 1;
    // عيّنات منتظمة عبر النافذة — يعمل مع أي min/max حتى خارج مدى النقاط
    const SAMPLES = 32;
    const parts: string[] = [];
    for (let i = 0; i <= SAMPLES; i += 1) {
        const v = lo + (span * i) / SAMPLES;
        parts.push(`${toRgba(interpolateStops(v, stops))} ${((i / SAMPLES) * 100).toFixed(1)}%`);
    }
    return `linear-gradient(to right, ${parts.join(', ')})`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function getHeatmapColor(value: number, type: ForecastGridType): string | null {
    // قيم الرياح/الهبّات تصل من الخادم بوحدة km/h أصلاً (wind_speed_unit=kmh)،
    // ومفتاح ألوان Zoom Earth معرّف بـ km/h — فلا حاجة لأي تحويل.
    const stops = STOPS_REGISTRY[type];
    if (!stops || stops.length === 0) return null;
    if (HIDE_BELOW_FIRST_STOP.has(type) && value < stops[0][0]) return null;
    return toRgba(interpolateStops(value, stops));
}

// ─────────────────────────────────────────────────────────────────────────────
// PARTICLE COLORS — Zoom Earth style: أبيض ساطع شبه شفاف يتغير بالسرعة
//
// في Zoom Earth، الجسيمات دائماً أبيض/تركواز فاتح جداً بغض النظر عن الطبقة.
// الشفافية تزداد بزيادة سرعة الرياح لتبرز التيارات النفاثة والعواصف.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * لون جسيمة الرياح — أبيض ساطع مع شفافية تتناسب مع السرعة
 * مُستخدَم لجميع الطبقات (Zoom Earth style)
 */
export function getWindParticleColor(speed: number, baseAlpha: number): string {
    // سرعة الرياح تأتي عادة بالـ m/s، نحولها لـ km/h لضبط الشفافية بدقة
    const speedKmh = speed * 3.6;

    // الشفافية تزداد تدريجياً مع السرعة (تيارات قوية = جسيمات أوضح)
    const speedFactor = Math.min(1.0, speedKmh / 120);
    const alpha = Math.min(0.98, baseAlpha * 0.40 + speedFactor * 0.60);

    // أبيض مع لمسة تركواز خفيفة جداً للسرعات العالية (Jet Stream effect)
    if (speedKmh > 90) {
        return `rgba(232, 248, 255, ${alpha.toFixed(3)})`;
    }
    return `rgba(255, 255, 255, ${alpha.toFixed(3)})`;
}

/**
 * لون RGB الخام لجسيمة الرياح (للحالات التي تحتاج RGB منفصل)
 */
export function getWindParticleRgb(speed: number): [number, number, number] {
    return speed * 3.6 > 90 ? [232, 248, 255] : [255, 255, 255];
}

/**
 * لون جسيمة طبقة الهطول (تُستخدَم في wind-advection mode)
 * نفس اللون الأبيض / تركواز فاتح مثل بقية الطبقات
 */
export function getPrecipParticleColor(intensity: number, baseAlpha: number): string {
    // للهطول: نفس الجسيمات البيضاء، لكن مع تمييز بسيط بالشفافية
    const speedFactor = Math.min(1.0, intensity / 15);
    const alpha = Math.min(0.90, baseAlpha * 0.60 + speedFactor * 0.35);
    return `rgba(220, 245, 255, ${alpha.toFixed(3)})`;
}

/**
 * لون جسيمة الطبقات الـscalar (temperature, pressure, humidity, etc.)
 * مُوحَّد: أبيض ساطع مثل Zoom Earth — لا اختلاف بين الطبقات
 */
export function getScalarParticleColor(_type: ForecastGridType, baseAlpha: number): string {
    // توحيد تام مع Zoom Earth: أبيض ساطع دائماً
    const alpha = Math.min(0.88, baseAlpha * 0.80);
    return `rgba(255, 255, 255, ${alpha.toFixed(3)})`;
}
